// Recursively inject compact number formatting ("~s": SI prefix, e.g. 14M, 50K)
// on any quantitative axis encoding that does not already have an explicit format.
// Only touches `type: "quantitative"` channels so temporal/ordinal axes are unaffected.
export function injectCompactAxisFormat(spec: Record<string, unknown>): void {
  if (spec.encoding && typeof spec.encoding === 'object') {
    const encoding = spec.encoding as Record<string, unknown>;
    for (const channel of ['x', 'y', 'x2', 'y2']) {
      const enc = encoding[channel] as Record<string, unknown> | undefined;
      if (enc && enc.type === 'quantitative') {
        const axis = (enc.axis as Record<string, unknown> | null) ?? {};
        if (!axis.format) {
          enc.axis = { ...axis, format: '~s' };
        }
      }
    }
  }

  for (const key of ['layer', 'concat', 'hconcat', 'vconcat', 'spec']) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const sub of child) {
        if (typeof sub === 'object' && sub !== null) {
          injectCompactAxisFormat(sub as Record<string, unknown>);
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      injectCompactAxisFormat(child as Record<string, unknown>);
    }
  }
}

const pathMarkTypes = ['line', 'area'];

const hasConfiguredPoints = (
  config: Record<string, unknown> | undefined,
  markType: string,
): boolean => {
  const markConfig = config?.[markType];
  return (
    typeof markConfig === 'object' &&
    markConfig !== null &&
    'point' in markConfig
  );
};

const hasShapeEncoding = (spec: Record<string, unknown>): boolean => {
  const encoding = spec.encoding;
  return (
    typeof encoding === 'object' && encoding !== null && 'shape' in encoding
  );
};

const enableMarkTooltip = (
  spec: Record<string, unknown>,
  config: Record<string, unknown> | undefined,
): void => {
  const markValue = spec.mark;
  let mark: Record<string, unknown> | undefined;
  if (typeof markValue === 'string') {
    mark = { type: markValue };
  } else if (typeof markValue === 'object' && markValue !== null) {
    mark = markValue as Record<string, unknown>;
  }

  if (!mark) return;

  const markType = String(mark.type);
  mark.tooltip ??= true;
  if (
    pathMarkTypes.includes(markType) &&
    mark.point === undefined &&
    !hasShapeEncoding(spec) &&
    !hasConfiguredPoints(config, markType)
  ) {
    mark.point = 'transparent';
  }
  spec.mark = mark;
};

export const injectTooltipInteractivity = (
  spec: Record<string, unknown>,
): void => {
  const isVegaLite =
    typeof spec.$schema === 'string' && spec.$schema.includes('vega-lite');
  if (!isVegaLite) return;

  const config =
    typeof spec.config === 'object' && spec.config !== null
      ? (spec.config as Record<string, unknown>)
      : undefined;

  if (Array.isArray(spec.layer)) {
    for (const layer of spec.layer) {
      if (typeof layer === 'object' && layer !== null) {
        enableMarkTooltip(layer as Record<string, unknown>, config);
      }
    }
    return;
  }

  enableMarkTooltip(spec, config);
};
